// Bug / Improve — on every page, in the header (Jacky, 12 October 2026: "Build a Bug/Improvement
// button on every page in the main top header, input text and annotate what is needed to
// fix/improve").
//
// One button, one small dialog: what kind, what is wrong or wanted in his words, and "point at it
// on the page" — the dialog steps aside, he clicks the element he means, and it comes back with the
// element named and placed (lib/feedback-annotate.ts). The page he is on, the viewport, the language
// and the text size are captured for him, never typed. The report is one `raise_feedback`
// proposal; a staff click is the approval (the backbone's staff catch-all policy), so it executes
// and EmmA has it: she reads the queue with `feedback_reports` and reports back with
// `set_feedback_status`. Nothing here is customer communication, so nothing comes back for a click.
import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {Bug, MousePointerClick, X} from 'lucide-react';
import {Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Textarea, cn} from '@aiw/ui';
import {api, authHeaders, client, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useMe} from '../hooks/useMe';
import {useT} from '../lib/i18n';
import {useTextSize} from '../lib/text-size';
import {annotate, pageContext, type Annotation} from '../lib/feedback-annotate';

type FeedbackKind = 'bug' | 'improvement';
type FeedbackRow = {id: string; kind: FeedbackKind; body: string; page_path: string; status: string; status_note: string; created_at: string};
const KINDS = ['bug', 'improvement'] as const;
const STATUSES = ['open', 'acknowledged', 'done', 'declined'] as const;
type Status = (typeof STATUSES)[number];

const when = (iso: string) => new Date(iso).toLocaleString('en-AU', {timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'});

export function FeedbackButton() {
  const {t, lang} = useT();
  const {size} = useTextSize();
  const me = useMe();
  const location = useLocation();
  const queries = useQueryClient();

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [body, setBody] = useState('');
  const [marks, setMarks] = useState<Annotation[]>([]);
  const [picking, setPicking] = useState(false);
  const [hover, setHover] = useState<{x: number; y: number; w: number; h: number} | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{tone: 'ok' | 'warn'; text: string} | null>(null);
  const attempt = useRef<{payload: string; key: string} | null>(null);

  /** The page the report is about is the page he pressed the button on — frozen at open, so a
   *  pick or a redirect while the dialog is up cannot move it. */
  const [page, setPage] = useState({path: '/', title: ''});
  const openDialog = () => {
    setPage({path: `${location.pathname}${location.search}`, title: typeof document === 'undefined' ? '' : document.title});
    setNotice(null);
    setOpen(true);
  };

  /** GET /api/feedback is a statement route (the typed chain is at TS's depth limit), so it is read the way the other late routes are. */
  const recent = useQuery({queryKey: ['feedback'], enabled: open, staleTime: 15_000, queryFn: async () => {
    const r = await fetchImpl(`${env.apiBase}/api/feedback`, {headers: await authHeaders()});
    const b = (await r.json()) as {data?: unknown};
    if (!r.ok || !Array.isArray(b.data)) throw new Error('Reports unavailable');
    return b.data as FeedbackRow[];
  }});

  /** POINTING. The dialog steps aside; the next click anywhere on the page is the element he means.
   *  Capture phase, so the page never sees the click (nothing navigates, nothing submits). Esc stops. */
  useEffect(() => {
    if (!picking) return;
    const move = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const r = el?.getBoundingClientRect();
      setHover(r ? {x: r.x, y: r.y, w: r.width, h: r.height} : null);
    };
    const click = (e: MouseEvent) => {
      e.preventDefault(); e.stopPropagation();
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el) setMarks((m) => (m.length >= 20 ? m : [...m, annotate(el)]));
      setPicking(false); setHover(null); setOpen(true);
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setPicking(false); setHover(null); setOpen(true); } };
    document.addEventListener('mousemove', move, true);
    document.addEventListener('click', click, true);
    document.addEventListener('keydown', key, true);
    const prev = document.body.style.cursor; document.body.style.cursor = 'crosshair';
    return () => {
      document.removeEventListener('mousemove', move, true);
      document.removeEventListener('click', click, true);
      document.removeEventListener('keydown', key, true);
      document.body.style.cursor = prev;
    };
  }, [picking]);

  const args = useMemo(() => ({
    kind, body: body.trim(), page_path: page.path, page_title: page.title, annotations: marks,
    context: typeof window === 'undefined' ? {} : pageContext(window, {lang, text_size: size, app: 'crm'}),
    raised_by_email: me.data?.user.email ?? null,
  }), [kind, body, page, marks, lang, size, me.data?.user.email]);

  async function record() {
    if (!args.body) return;
    setBusy(true); setNotice(null);
    try {
      // The same wording twice is the same report (a retry), never two rows.
      const payload = JSON.stringify(args);
      if (attempt.current?.payload !== payload) attempt.current = {payload, key: crypto.randomUUID()};
      const r = await api.propose(client, {command: 'raise_feedback', args, idempotency_key: attempt.current.key});
      if (r.status === 'executed' || r.status === 'pending') {
        setNotice({tone: 'ok', text: r.status === 'executed' ? t('feedback.recorded') : t('feedback.pending')});
        setBody(''); setMarks([]); attempt.current = null;
        await queries.invalidateQueries({queryKey: ['feedback']});
      } else {
        setNotice({tone: 'warn', text: t('feedback.failed', {reason: r.reason})});
      }
    } catch (e) {
      setNotice({tone: 'warn', text: t('feedback.failed', {reason: e instanceof Error ? e.message : String(e)})});
    } finally { setBusy(false); }
  }

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        title={t('feedback.title')}
        aria-label={t('feedback.title')}
        className="flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-sm text-muted-foreground hover:bg-muted"
      >
        <Bug className="h-4 w-4" />
        <span className="hidden xl:inline">{t('feedback.button')}</span>
      </button>

      {/* The outline under the pointer while he is choosing. Fixed, above everything, never catches the click. */}
      {picking && (
        <div className="pointer-events-none fixed inset-0 z-[60]" aria-live="polite">
          {hover && <div className="absolute rounded-sm ring-2 ring-primary ring-offset-1" style={{left: hover.x, top: hover.y, width: hover.w, height: hover.h}} />}
          <p className="absolute left-1/2 top-3 -translate-x-1/2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow">{t('feedback.pointing')}</p>
        </div>
      )}

      <Dialog open={open && !picking} onOpenChange={(o) => { if (!picking) setOpen(o); }}>
        <DialogContent className="max-w-xl [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{t('feedback.title')}</DialogTitle>
            <DialogDescription>{t('feedback.intro')}</DialogDescription>
          </DialogHeader>

          <div className="flex items-center rounded-md border text-sm" role="group" aria-label={t('feedback.title')}>
            {KINDS.map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}
                className={cn('flex-1 px-3 py-1.5 first:rounded-l-md last:rounded-r-md', kind === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}>
                {t(`feedback.kind.${k}`)}
              </button>
            ))}
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('feedback.describe')}</span>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} rows={5} autoFocus placeholder={t(`feedback.placeholder.${kind}`)} />
          </label>

          <div className="text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)} disabled={marks.length >= 20}>
                <MousePointerClick className="h-4 w-4" />{t('feedback.point')}
              </Button>
              <span className="text-muted-foreground"><span className="font-medium text-foreground">{t('feedback.page')}</span> <span className="font-mono text-micro">{page.path}</span></span>
            </div>
            {marks.length > 0 && (
              <ul className="mt-2 space-y-1" aria-label={t('feedback.pointed')}>
                {marks.map((m, i) => (
                  <li key={i} className="flex min-w-0 items-start gap-2 overflow-hidden rounded border bg-muted/40 px-2 py-1">
                    <span className="min-w-0 flex-1 overflow-hidden"><span className="block truncate font-medium">{m.label}</span>{m.text && m.text !== m.label && <span className="block truncate text-muted-foreground">{m.text}</span>}<span className="block truncate font-mono text-micro text-muted-foreground">{m.path}</span></span>
                    <button type="button" aria-label={t('feedback.remove')} title={t('feedback.remove')} onClick={() => setMarks((all) => all.filter((_, j) => j !== i))} className="rounded p-0.5 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            {notice ? <p role="status" className={cn('text-sm', notice.tone === 'warn' ? 'text-warning' : 'text-muted-foreground')}>{notice.text}</p> : <span />}
            <Button type="button" onClick={() => void record()} disabled={busy || !body.trim()}>{busy ? t('feedback.sending') : t('feedback.send')}</Button>
          </div>

          <section className="border-t pt-3 text-sm">
            <h3 className="mb-1.5 font-medium">{t('feedback.recent')}</h3>
            {recent.isError ? <p className="text-muted-foreground">{t('feedback.loadFailed')}</p>
              : !recent.data?.length ? <p className="text-muted-foreground">{recent.isPending ? '…' : t('feedback.none')}</p>
              : <ul className="max-h-40 space-y-1 overflow-y-auto">{recent.data.slice(0, 8).map((f) => <RecentRow key={f.id} f={f} />)}</ul>}
          </section>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RecentRow({f}: {f: FeedbackRow}) {
  const {t} = useT();
  const status = (STATUSES as readonly string[]).includes(f.status) ? (f.status as Status) : 'open';
  return (
    <li className="flex min-w-0 items-start gap-2 overflow-hidden">
      <span className={cn('mt-0.5 shrink-0 rounded px-1.5 font-mono text-micro', status === 'open' ? 'bg-warning/15 text-warning' : status === 'done' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>{t(`feedback.status.${status}`)}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate">{f.body}</span>
        <span className="block truncate font-mono text-micro text-muted-foreground">{t(`feedback.kind.${f.kind}`)} · {f.page_path} · {when(f.created_at)}{f.status_note ? ` · ${f.status_note}` : ''}</span>
      </span>
    </li>
  );
}
